/**
 * The persisted media preferences, plus the wiring that pushes a change onto a
 * live `MediaClient`.
 *
 * The client is passed as a REF, not a value: the session that owns the client
 * needs the preferences in order to connect, so a plain value argument would be
 * a cycle. Callers assign `ref.current` during render; every effect here runs
 * afterwards and therefore always sees it.
 *
 * With no ref (pre-join) this only persists, and the session applies the stored
 * values on connect.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DeviceKind, MediaClient } from '../media/contract';
import { loadMediaPreferences, saveMediaPreferences } from '../media/settings/preferenceStorage';
import type {
  AudioProcessingPreferences, MediaPreferences, VideoEffectPreferences,
} from '../media/settings/preferences';
import { useSharePresence } from './useSharePresence';

const DEVICE_FIELD: Readonly<Record<DeviceKind, keyof MediaPreferences['devices']>> = {
  videoinput: 'cameraId',
  audioinput: 'micId',
  audiooutput: 'speakerId',
};

export interface MediaPreferencesApi {
  readonly preferences: MediaPreferences;
  setAudio(patch: Partial<AudioProcessingPreferences>): void;
  setVideo(patch: Partial<VideoEffectPreferences>): void;
  setDevice(kind: DeviceKind, deviceId: string): void;
  setLowBandwidth(on: boolean): void;
}

export type MediaClientRef = { current: MediaClient | null };

const DETACHED: MediaClientRef = { current: null };

export function useMediaPreferences(clientRef: MediaClientRef = DETACHED): MediaPreferencesApi {
  const [preferences, setPreferences] = useState<MediaPreferences>(loadMediaPreferences);
  /** Seeded with the mounted values so connect()'s own application is not redone. */
  const applied = useRef({ audio: preferences.audio, video: preferences.video });

  useEffect(() => {
    saveMediaPreferences(preferences);
  }, [preferences]);

  /**
   * Presenting/Screen sharing presence, wired HERE because this is the one hook
   * both live rooms hand their client to — `useLiveMeeting` for a meeting and
   * `useCallRoom` for a call — so one implementation covers both and the two can
   * never drift. Detached (pre-join) it does nothing.
   */
  useSharePresence(clientRef);

  useEffect(() => {
    const client = clientRef.current;
    if (!client || applied.current.audio === preferences.audio) return;
    applied.current.audio = preferences.audio;
    void client.setAudioProcessing(preferences.audio);
  }, [clientRef, preferences.audio]);

  useEffect(() => {
    const client = clientRef.current;
    if (!client || applied.current.video === preferences.video) return;
    applied.current.video = preferences.video;
    void client.setVideoEffect(preferences.video);
  }, [clientRef, preferences.video]);

  const setAudio = useCallback((patch: Partial<AudioProcessingPreferences>) => {
    setPreferences((previous) => ({ ...previous, audio: { ...previous.audio, ...patch } }));
  }, []);

  const setVideo = useCallback((patch: Partial<VideoEffectPreferences>) => {
    setPreferences((previous) => ({ ...previous, video: { ...previous.video, ...patch } }));
  }, []);

  const setDevice = useCallback(
    (kind: DeviceKind, deviceId: string) => {
      setPreferences((previous) => ({
        ...previous,
        devices: { ...previous.devices, [DEVICE_FIELD[kind]]: deviceId },
      }));
      void clientRef.current?.setDevice(kind, deviceId);
    },
    [clientRef],
  );

  const setLowBandwidth = useCallback((lowBandwidth: boolean) => {
    setPreferences((previous) => ({ ...previous, lowBandwidth }));
  }, []);

  return useMemo(
    () => ({ preferences, setAudio, setVideo, setDevice, setLowBandwidth }),
    [preferences, setAudio, setVideo, setDevice, setLowBandwidth],
  );
}
